import SectionsAPIService from './section-api-services.js';
import { localizeUrl } from '@theme/localize-url';

const hasCompareVariantId = value => value !== null && value !== undefined && value !== '';

const compareItemMatches = (product, variantId, handle) => {
  if (hasCompareVariantId(variantId)) {
    return String(product.id) === String(variantId);
  }

  return Boolean(handle) && product.handle === handle;
};

const parseHTML = (html, selector) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.querySelector(selector);
};

export const getCompareProducts = () => {
  try {
    const storedData = localStorage.getItem('compareProducts');
    const parsed = storedData ? JSON.parse(storedData) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export class CompareCheckbox extends HTMLElement {
  constructor() {
    super();
    this.toggleCompare = this.toggleCompare.bind(this);
  }

  connectedCallback() {
    this.initCheckState();
    this.addEventListener('click', this.toggleCompare);
    document.addEventListener('theme:compare:update', () => this.initCheckState());
  }

  initCheckState() {
    const products = getCompareProducts();
    const handle = this.getAttribute('data-product-handle');
    const variantId = this.closest('product-card')?.getAttribute('data-variant-id');
    if (!variantId && !handle) return;

    const isCompared = products.some(p => compareItemMatches(p, variantId, handle));
    this.classList.toggle('is-checked', isCompared);

    const input = this.querySelector('input');
    if (input) input.checked = isCompared;
  }

  toggleCompare(e) {
    e.preventDefault();
    const card = this.closest('product-card');
    const variantId = card?.getAttribute('data-variant-id');
    const handle = this.getAttribute('data-product-handle');
    const url = this.getAttribute('data-product-url');

    if (!handle) return;

    const products = getCompareProducts();
    const isAdded = products.some(p => compareItemMatches(p, variantId, handle));

    if (isAdded) {
      this.removeProduct(variantId, handle);
    } else {
      this.addProduct(variantId, handle, url);
    }
  }

  addProduct(variantId, handle, url) {
    const products = getCompareProducts();
    const limit = 3;

    if (products.length >= limit) {
      alert(`Limit reached. Max ${limit} products.`);
      return;
    }

    const nextProduct = { handle: handle, url: url };
    if (hasCompareVariantId(variantId)) {
      nextProduct.id = variantId;
    }

    const updated = [...products, nextProduct];
    localStorage.setItem('compareProducts', JSON.stringify(updated));

    document.dispatchEvent(new CustomEvent('theme:compare:add'));
    document.dispatchEvent(new CustomEvent('theme:compare:update'));
  }

  removeProduct(variantId, handle) {
    const products = getCompareProducts();
    const updated = products.filter(p => !compareItemMatches(p, variantId, handle));
    localStorage.setItem('compareProducts', JSON.stringify(updated));

    document.dispatchEvent(new CustomEvent('theme:compare:remove'));
    document.dispatchEvent(new CustomEvent('theme:compare:update'));
  }
}

export class CompareBar extends HTMLElement {
  connectedCallback() {
    this.limit = 3;
    this.pageType = this.getAttribute('data-page-type');
    this.renderInitialProducts();

    document.addEventListener('theme:compare:add', () => this.refreshBar());
    document.addEventListener('theme:compare:remove', () => this.refreshBar());
    document.addEventListener('theme:compare:update', () => this.updateUI());

    this.addEventListener('click', (e) => this.handleClicks(e));
    this.initSlider();
  }

  async renderInitialProducts() {
    await this.refreshBar();
    await this.initSlider();
  }

  async refreshBar() {
    const products = getCompareProducts();
    const container = this.querySelector('.js-compare-bar__products');
    if (!container) return;

    const currentProductEls = Array.from(container.querySelectorAll('.js-compare-bar-product-element'));

    currentProductEls.forEach(el => {
      const elId = el.getAttribute('data-variant-id');
      const handle = el.getAttribute('data-product-handle');
      if (!products.some(p => compareItemMatches(p, elId, handle))) {
        el.closest('.js-compare-bar-product').remove();
      }
    });

    for (const item of products) {
      const existing = Array.from(container.querySelectorAll('.js-compare-bar-product-element')).find(el => {
        return compareItemMatches(item, el.getAttribute('data-variant-id'), el.getAttribute('data-product-handle'));
      });

      if (!existing) {
        await this.appendProductToContainer(container, item.url, item.id, item.handle);
      }
    }

    this.reorderSlots(container, products.length);
    this.updateUI();
  }

  async appendProductToContainer(container, productURL, variantId, handle) {
    try {
      const productPath = productURL.split('/products/').pop();
      const fetchUrl = `${window.location.origin}/products/${productPath}`;

      const html = await SectionsAPIService.fetch(fetchUrl, 'render-compare-product');
      const barElement = parseHTML(html, '.js-compare-bar-product-element');

      if (barElement) {
        if (hasCompareVariantId(variantId)) {
          barElement.setAttribute('data-variant-id', variantId);
        }

        if (handle) {
          barElement.setAttribute('data-product-handle', handle);
        }

        const slot = document.createElement('div');
        slot.className = 'compare-bar__product js-compare-bar-product';
        slot.appendChild(barElement);
        container.appendChild(slot);
      }
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }

  reorderSlots(container, activeCount) {
    Array.from(container.querySelectorAll('.js-compare-bar-product')).forEach(slot => {
      if (!slot.querySelector('.js-compare-bar-product-element')) {
        slot.remove();
      }
    });

    const emptyCount = Math.max(0, this.limit - activeCount);
    const emptyTemplate = document.getElementById('compare-bar-product-template');

    for (let i = 0; i < emptyCount; i++) {
      const slot = document.createElement('div');
      slot.className = 'compare-bar__product js-compare-bar-product is-empty';

      if (emptyTemplate) {
        slot.appendChild(document.importNode(emptyTemplate.content, true));
      }
      container.appendChild(slot);
    }
  }

  handleClicks(e) {
    const target = e.target;
    const removeBtn = target.closest('.js-compare-bar-remove-product');

    if (removeBtn) {
      const productEl = removeBtn.closest('.js-compare-bar-product-element');
      const variantId = productEl.getAttribute('data-variant-id');
      const handle = productEl.getAttribute('data-product-handle');

      const products = getCompareProducts();
      const updated = products.filter(p => !compareItemMatches(p, variantId, handle));
      localStorage.setItem('compareProducts', JSON.stringify(updated));

      this.refreshBar();
      document.dispatchEvent(new CustomEvent('theme:compare:update'));
      return;
    }

    if (target.classList.contains('js-compare-clear-all')) {
      localStorage.removeItem('compareProducts');
      this.refreshBar();
      document.dispatchEvent(new CustomEvent('theme:compare:update'));
      return;
    }

    if (target.closest('.js-expand-compare-accordion')) {
      this.classList.toggle('is-expanded');
    }

    if (target.closest('.js-compare-redirect')) {
      let collectionTitle = target.closest('.js-compare-redirect').getAttribute('data-collection-title');
      let pageUrl = window.location.href;

      if (this.pageType === 'search') {
        collectionTitle = Theme.translations?.search_results;
      }

      const breadcrumbData = {
        url: pageUrl,
        title: collectionTitle.trim()
      };
      localStorage.setItem('compareBreadcrumb', JSON.stringify(breadcrumbData));
    }
  }

  updateUI() {
    const products = getCompareProducts();
    const count = products.length;

    document.body.classList.toggle('compare-bar-active', count >= 2);
    document.body.classList.toggle('compare-is-disabled', count >= this.limit);

    document.querySelectorAll('.js-compare-count').forEach(el => {
        el.textContent = count;
    });

    const redirectBtns = this.querySelectorAll('.js-compare-redirect');
    if (redirectBtns.length > 0) {
      const ids = products.map(p => p.id).filter(Boolean).join(',');
      redirectBtns.forEach(btn => {
        const baseUrl = btn.getAttribute('data-base-url') || '/pages/compare-products';
        const finalUrl = `${baseUrl}?ids=${encodeURIComponent(ids)}`;

        btn.href = localizeUrl(finalUrl);
      });
    }
  }

  async initSlider() {
    const nextBtns = this.querySelectorAll('.js-arrow-next');
    const prevBtns = this.querySelectorAll('.js-arrow-prev');
    const mainGrid = this.querySelector('.js-compare-bar__products');

    const updateButtons = () => {
      if (!mainGrid) return;
      const scrollLeft = Math.ceil(mainGrid.scrollLeft);
      const maxScroll = mainGrid.scrollWidth - mainGrid.clientWidth;

      prevBtns.forEach(btn => btn.disabled = scrollLeft <= 5);
      nextBtns.forEach(btn => btn.disabled = scrollLeft >= maxScroll - 5);
    };

    mainGrid.addEventListener('scroll', () => {
      updateButtons();
    });

    const handleBtnScroll = (direction) => {
      if (!mainGrid) return;
      const cardWidth = window.innerWidth * 0.43;
      const gap = 12;
      mainGrid.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
    };

    nextBtns.forEach(btn => btn.addEventListener('click', () => handleBtnScroll(1)));
    prevBtns.forEach(btn => btn.addEventListener('click', () => handleBtnScroll(-1)));

    setTimeout(updateButtons, 200);
  }
}

customElements.define('compare-checkbox', CompareCheckbox);
customElements.define('compare-bar', CompareBar);